import ApiService from '../../services/api.service';
import { tradeLicenceService } from '../../services/clients/tradeLicence.service';
import { unwrapData } from '../../utils/apiConvert';

const pick = (obj, snake, camel) => (obj?.[snake] !== undefined ? obj[snake] : obj?.[camel]);

// Maps the organization-documents response onto the grouped shape the employee document cards read.
const toGroups = (payload) => {
  const groups = Array.isArray(payload?.groups) ? payload.groups : [];
  return groups.map((g) => {
    const c = g.common || {};
    const source = pick(c, 'uploaded_by_type', 'uploadedByType') === 'PORTAL_USER' ? 'PORTAL' : 'TC';
    const common = {
      documentType: pick(c, 'document_type', 'documentType'),
      version: c.version,
      isCurrent: pick(c, 'is_current', 'isCurrent'),
      documentNumber: pick(c, 'document_number', 'documentNumber'),
      issueDate: pick(c, 'issue_date', 'issueDate'),
      expiryDate: pick(c, 'expiry_date', 'expiryDate'),
      uploadedBySource: source,
      uploadedByName: pick(c, 'uploaded_by_name', 'uploadedByName'),
      uploadDate: pick(c, 'uploaded_at', 'uploadedAt')
    };
    return {
      documentGroupId: pick(g, 'document_group_id', 'documentGroupId'),
      common,
      documents: (g.documents || []).map((d) => ({
        id: d.id,
        fileName: pick(d, 'file_name', 'fileName'),
        filePath: pick(d, 'file_path', 'filePath'),
        version: common.version,
        uploadDate: common.uploadDate,
        uploadedBySource: source,
        uploadedByName: common.uploadedByName
      }))
    };
  });
};

const base = (orgId) => `/api/internal-organizations/${orgId}/documents`;

/**
 * Organization (company) documents of one client, for the typing center.
 *
 *   documents()                -> grouped documents ({ groups })
 *   upload(files, type, meta)  -> uploads one version
 *   view(filePath)             -> axios blob response
 *   remove(groupId)            -> deletes one version
 */
const portalAuth = () => ({ Authorization: `Bearer ${localStorage.getItem('portalToken')}` });
const PORTAL_BASE = '/api/portal/organization-documents';

/** The signed-in portal user's own organization documents (no delete). Same shape as the typing-center adapter. */
export const portalOrgDocumentApi = () => ({
  documents: async () => {
    const resp = await ApiService.get(PORTAL_BASE, { headers: portalAuth() });
    return { groups: toGroups(resp?.data?.data || {}) };
  },
  upload: (files, documentType, meta = {}) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    formData.append('document_type', documentType);
    if (meta.documentNumber) formData.append('document_number', meta.documentNumber);
    if (meta.issueDate) formData.append('issue_date', meta.issueDate);
    if (meta.expiryDate) formData.append('expiry_date', meta.expiryDate);
    return ApiService.post(PORTAL_BASE, formData, { headers: { ...portalAuth(), 'Content-Type': 'multipart/form-data' } });
  },
  extract: async (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    const resp = await ApiService.post(`${PORTAL_BASE}/extract`, formData, { headers: { ...portalAuth(), 'Content-Type': 'multipart/form-data' } });
    return unwrapData(resp);
  },
  view: (filePath) => ApiService.get(`${PORTAL_BASE}/view`, { params: { filePath }, responseType: 'blob', headers: portalAuth() })
});

export const tcOrgDocumentApi = (orgId) => ({
  extract: (file, documentType) => tradeLicenceService.extractDocument(file, documentType),
  documents: async () => {
    const resp = await ApiService.get(base(orgId));
    return { groups: toGroups(resp?.data?.data || {}) };
  },
  upload: (files, documentType, meta = {}) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    formData.append('document_type', documentType);
    if (meta.documentNumber) formData.append('document_number', meta.documentNumber);
    if (meta.issueDate) formData.append('issue_date', meta.issueDate);
    if (meta.expiryDate) formData.append('expiry_date', meta.expiryDate);
    if (meta.metadata) formData.append('metadata', meta.metadata);
    return ApiService.post(base(orgId), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  view: (filePath) => ApiService.get(`${base(orgId)}/view`, { params: { filePath }, responseType: 'blob' }),
  remove: (groupId) => ApiService.delete(`${base(orgId)}/${groupId}`)
});
